import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Bell, Plus, Trash2, MapPin, Zap, Lock, Send, Mail,
  Smartphone, ToggleLeft, ToggleRight, Settings,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import PricingDialog from "@/components/PricingDialog";

interface AlertRule {
  id: string;
  name: string;
  event_types: string[];
  radius_km: number;
  latitude: number;
  longitude: number;
  location_name: string | null;
  channels: { email: boolean; telegram: boolean; push: boolean };
  telegram_chat_id: string | null;
  enabled: boolean;
  created_at: string;
}

const EVENT_TYPES = [
  { value: "missile", label: "🚀 Missile Strike" },
  { value: "drone", label: "🛩️ Drone Attack" },
  { value: "air_raid", label: "🚨 Air Raid" },
  { value: "explosion", label: "💥 Explosion" },
  { value: "movement", label: "🪖 Military Movement" },
];

const SmartAlerts = ({ embedded }: { embedded?: boolean }) => {
  const [pricingOpen, setPricingOpen] = useState(false);
  const { user, isPremium } = useAuth();
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  // New rule form state
  const [newName, setNewName] = useState("My Alert Zone");
  const [newLocation, setNewLocation] = useState("");
  const [newCoords, setNewCoords] = useState<{ lat: number; lng: number; name: string } | null>(null);
  const [newRadius, setNewRadius] = useState([200]);
  const [newTypes, setNewTypes] = useState<string[]>(["missile", "drone", "air_raid", "explosion"]);
  const [newChannels, setNewChannels] = useState({ email: true, telegram: false, push: true });
  const [newTelegramId, setNewTelegramId] = useState("");
  const [resolving, setResolving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);

  useEffect(() => {
    setPushSupported("Notification" in window);
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchRules();
  }, [user]);

  const fetchRules = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("alert_rules")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      console.error("Failed to fetch alert rules:", error);
    } else {
      setRules((data as unknown as AlertRule[]) ?? []);
    }
    setLoading(false);
  };

  const resolveLocation = async () => {
    if (!newLocation.trim()) return;
    setResolving(true);
    try {
      const { data, error } = await supabase.functions.invoke("geocode", {
        body: { query: newLocation },
      });
      if (error) throw error;
      if (data?.found) {
        setNewCoords({ lat: data.lat, lng: data.lng, name: data.name });
        toast.success(`📍 ${data.name}, ${data.country}`);
      } else {
        toast.error("Location not found");
      }
    } catch {
      toast.error("Geocoding failed");
    }
    setResolving(false);
  };

  const createRule = async () => {
    if (!newCoords) {
      toast.error("Please set a location first");
      return;
    }
    if (newTypes.length === 0) {
      toast.error("Select at least one event type");
      return;
    }
    setSaving(true);

    // Request push permission if push channel enabled
    if (newChannels.push && pushSupported && Notification.permission === "default") {
      await Notification.requestPermission();
    }

    const { error } = await supabase.from("alert_rules").insert({
      user_id: user!.id,
      name: newName,
      event_types: newTypes,
      radius_km: newRadius[0],
      latitude: newCoords.lat,
      longitude: newCoords.lng,
      location_name: newCoords.name,
      channels: newChannels,
      telegram_chat_id: newChannels.telegram ? newTelegramId : null,
      enabled: true,
    });

    if (error) {
      toast.error("Failed to create alert rule");
    } else {
      toast.success("✅ Alert rule created!");
      setShowCreate(false);
      resetForm();
      fetchRules();
    }
    setSaving(false);
  };

  const toggleRule = async (id: string, enabled: boolean) => {
    await supabase.from("alert_rules").update({ enabled: !enabled }).eq("id", id);
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, enabled: !enabled } : r)));
  };

  const deleteRule = async (id: string) => {
    await supabase.from("alert_rules").delete().eq("id", id);
    setRules((prev) => prev.filter((r) => r.id !== id));
    toast.success("Alert rule deleted");
  };

  const resetForm = () => {
    setNewName("My Alert Zone");
    setNewLocation("");
    setNewCoords(null);
    setNewRadius([200]);
    setNewTypes(["missile", "drone", "air_raid", "explosion"]);
    setNewChannels({ email: true, telegram: false, push: true });
    setNewTelegramId("");
  };

  if (!isPremium && !embedded) {
    return (
      <section className="border-b border-border">
        <div className="container mx-auto px-4 py-12">
          <div className="relative overflow-hidden rounded-xl border border-border bg-card p-8 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-warning/5 to-transparent" />
            <div className="relative">
              <Lock className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h2 className="mb-2 text-2xl font-bold text-foreground">🚨 Smart Alerts</h2>
              <p className="mb-2 text-muted-foreground max-w-lg mx-auto">
                Get instant notifications when conflict events are detected near locations you care about.
              </p>
              <div className="mb-6 flex flex-wrap justify-center gap-2">
                <Badge variant="outline">Telegram</Badge>
                <Badge variant="outline">Email</Badge>
                <Badge variant="outline">Push</Badge>
              </div>
              <Button
                onClick={() => setPricingOpen(true)}
                className="bg-primary text-primary-foreground"
              >
                <Zap className="mr-2 h-4 w-4" /> Unlock Smart Alerts
              </Button>
            </div>
          </div>
        </div>
        <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
      </section>
    );
  }

  const Wrapper = embedded ? "div" : "section";
  const wrapperProps = embedded ? {} : { id: "smart-alerts", className: "border-b border-border" };

  return (
    <Wrapper {...wrapperProps}>
      <div className={embedded ? "" : "container mx-auto px-4 py-8"}>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Bell className="h-6 w-6 text-warning" /> Smart Alerts
            </h2>
            <p className="text-sm text-muted-foreground">
              Get notified when threats are detected near your locations
            </p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-primary text-primary-foreground">
                <Plus className="mr-1 h-4 w-4" /> New Alert
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md bg-card border-border">
              <DialogHeader>
                <DialogTitle>Create Alert Rule</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">
                    Alert Name
                  </label>
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="bg-background"
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">
                    Center Location
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={newLocation}
                      onChange={(e) => setNewLocation(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && resolveLocation()}
                      placeholder="City name..."
                      className="bg-background"
                    />
                    <Button onClick={resolveLocation} disabled={resolving} size="sm" variant="secondary">
                      <MapPin className="h-4 w-4" />
                    </Button>
                  </div>
                  {newCoords && (
                    <p className="mt-1 text-xs text-primary">
                      📍 {newCoords.name} ({newCoords.lat.toFixed(2)}, {newCoords.lng.toFixed(2)})
                    </p>
                  )}
                </div>

                {/* Radius */}
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">
                    Radius: {newRadius[0]} km
                  </label>
                  <Slider value={newRadius} onValueChange={setNewRadius} min={50} max={500} step={25} />
                </div>

                {/* Event Types */}
                <div>
                  <label className="mb-2 block text-xs font-medium uppercase text-muted-foreground">
                    Event Types
                  </label>
                  <div className="space-y-2">
                    {EVENT_TYPES.map((et) => (
                      <label key={et.value} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={newTypes.includes(et.value)}
                          onCheckedChange={(checked) => {
                            setNewTypes((prev) =>
                              checked ? [...prev, et.value] : prev.filter((t) => t !== et.value)
                            );
                          }}
                        />
                        {et.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Channels */}
                <div>
                  <label className="mb-2 block text-xs font-medium uppercase text-muted-foreground">
                    Notification Channels
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4" /> Email
                      </span>
                      <Switch
                        checked={newChannels.email}
                        onCheckedChange={(c) => setNewChannels((p) => ({ ...p, email: c }))}
                      />
                    </label>
                    <label className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-sm">
                        <Send className="h-4 w-4" /> Telegram
                      </span>
                      <Switch
                        checked={newChannels.telegram}
                        onCheckedChange={(c) => setNewChannels((p) => ({ ...p, telegram: c }))}
                      />
                    </label>
                    {newChannels.telegram && (
                      <div className="ml-6">
                        <Input
                          value={newTelegramId}
                          onChange={(e) => setNewTelegramId(e.target.value)}
                          placeholder="Telegram Chat ID"
                          className="bg-background text-sm"
                        />
                        <p className="mt-1 text-xs text-muted-foreground">
                          Send /start to @ConflictMonitorBot, then paste your chat ID
                        </p>
                      </div>
                    )}
                    {pushSupported && (
                      <label className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-sm">
                          <Smartphone className="h-4 w-4" /> Browser Push
                        </span>
                        <Switch
                          checked={newChannels.push}
                          onCheckedChange={(c) => setNewChannels((p) => ({ ...p, push: c }))}
                        />
                      </label>
                    )}
                  </div>
                </div>

                <Button onClick={createRule} disabled={saving || !newCoords} className="w-full bg-primary text-primary-foreground">
                  {saving ? "Creating..." : "Create Alert Rule"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Rules List */}
        {loading ? (
          <div className="text-center text-muted-foreground py-8">Loading alerts...</div>
        ) : rules.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-card/50 p-12 text-center">
            <Bell className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">No alert rules yet. Create your first one!</p>
            <Button onClick={() => setShowCreate(true)} variant="secondary" size="sm">
              <Plus className="mr-1 h-4 w-4" /> Create Alert
            </Button>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {rules.map((rule) => (
              <div
                key={rule.id}
                className={`rounded-lg border bg-card p-4 transition-all ${
                  rule.enabled ? "border-primary/30" : "border-border opacity-60"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-foreground">{rule.name}</h3>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {rule.location_name || `${rule.latitude.toFixed(2)}, ${rule.longitude.toFixed(2)}`}
                      <span className="text-primary">• {rule.radius_km} km</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => toggleRule(rule.id, rule.enabled)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      {rule.enabled ? (
                        <ToggleRight className="h-5 w-5 text-primary" />
                      ) : (
                        <ToggleLeft className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => deleteRule(rule.id)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-1">
                  {rule.event_types.map((t) => (
                    <Badge key={t} variant="outline" className="font-mono text-xs">
                      {t}
                    </Badge>
                  ))}
                </div>

                <div className="mt-2 flex gap-2">
                  {rule.channels.email && (
                    <Badge className="bg-secondary text-secondary-foreground text-xs">
                      <Mail className="mr-1 h-3 w-3" /> Email
                    </Badge>
                  )}
                  {rule.channels.telegram && (
                    <Badge className="bg-secondary text-secondary-foreground text-xs">
                      <Send className="mr-1 h-3 w-3" /> Telegram
                    </Badge>
                  )}
                  {rule.channels.push && (
                    <Badge className="bg-secondary text-secondary-foreground text-xs">
                      <Smartphone className="mr-1 h-3 w-3" /> Push
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default SmartAlerts;
